import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-GB';
import { fetchLedger, fetchBillDetail } from '../../services/backendAPI';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CollapsibleMobileCard from '../Common/CollapsibleMobileCard';

const LedgerDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Table data state
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [billDetails, setBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Filter states
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [billId, setBillId] = useState('2021');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const formatDate = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateTimeStr;
    }
  };
  
  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateTimeStr;
    }
  };
  
  const formatBalance = (value) => {
    if (value === 0 || value === '0' || value === null || value === undefined) {
      return '0.00';
    }
    // Ensure exactly 2 decimal places
    return Number(value);
  };
  
  // Add a new function for formatting rates and amounts
  const formatAmount = (value) => {
    if (value === 0 || value === '0' || value === null || value === undefined) {
      return '0.00';
    }
    // Always show exactly 2 decimal places
    return Number(value);
  };
  
  useEffect(() => {
    if (location.state?.filters) {
      const { startDate, endDate, billId } = location.state.filters;
      setStartDate(new Date(startDate));
      setEndDate(new Date(endDate));
      setBillId(billId);
    }
    fetchLedgerData();
  }, [location.state]);
  
  const fetchLedgerData = async (overrideParams = null) => {
    setLoading(true);
    try {
      const currentPage = overrideParams?.page !== undefined ? overrideParams.page : page;
      const response = await fetchLedger({
        start_date: format(overrideParams?.startDate || startDate, 'yyyy-MM-dd'),
        end_date: format(overrideParams?.endDate || endDate, 'yyyy-MM-dd'),
        page: currentPage + 1,
        page_size: rowsPerPage,
        bill_id: overrideParams?.billId || billId
      });
      console.log("response",response);
      if (response?.data) {
        setLedgerData(response.data || []);
        setTotalCount(response.total_count||0);
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      enqueueSnackbar(error.message || 'Error fetching ledger data', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchLedgerData();
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    fetchLedgerData();
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchLedgerData();
  };
  
  const handleResetFilters = () => {
    // Define the reset values
    const defaultStartDate = new Date();
    const defaultEndDate = new Date();
    const defaultBillId = '2021';
    
    // Reset the state values
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setBillId(defaultBillId);
    setPage(0);
    
    // Fetch new data with reset values - pass as overrideParams to use immediately
    fetchLedgerData({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      billId: defaultBillId,
      page: 0
    });
  };

  const handleTransactionClick = async (transaction) => {
    setSelectedTransaction(transaction);
    if (isMobile) {
      setIsDrawerOpen(true);
    }
    
    // Fetch bill details if it's a bill transaction
    if (transaction.ledger_type === 'BILL') {
      setLoadingBillDetails(true);
      try {
        const response = await fetchBillDetail({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          page: 1,
          page_size: 20,
          bill_id: transaction.bill_id
        });
        if (response?.data) {
          setBillDetails(response.data);
        }
      } catch (error) {
        console.error('Error fetching bill details:', error);
        enqueueSnackbar('Error fetching bill details', { 
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 3000
        });
      } finally {
        setLoadingBillDetails(false);
      }
    }
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedTransaction(null);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTransaction(null);
  };
  
  const renderBillDetails = () => {
    if (!selectedTransaction || selectedTransaction.ledger_type !== 'BILL') return null;
    
    if (loadingBillDetails) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!billDetails || billDetails.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No bill details available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        p: 1.5,
        bgcolor: 'background.default',
        borderRadius: 1,
        mt: 2
      }}>
        {billDetails.map((detail, index) => (
          <Accordion 
            key={index}
            defaultExpanded={selectedTransaction.ledger_type === 'BILL'}
            sx={{ 
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&.Mui-expanded': {
                margin: '0 0 8px 0'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Bill ID: {detail.BillID}
                </Typography>
                <Chip 
                  label={detail.Exchange} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Basic Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Basic Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Client ID</Typography>
                      <Typography variant="body2">{detail.ClientID}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Created At</Typography>
                      <Typography variant="body2">{formatDateTime(detail.CreatedAt)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Trading Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Trading Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Trading Symbol</Typography>
                      <Typography variant="body2">{detail.TradingSymbol}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{detail.Description}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Buy Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Buy Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Quantity</Typography>
                      <Typography variant="body2">{detail.BuyQty}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Rate</Typography>
                      <Typography variant="body2">{formatAmount(detail.BuyRate)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">{formatAmount(detail.BuyAmount)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Sell Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sell Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Quantity</Typography>
                      <Typography variant="body2">{detail.SellQty}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Rate</Typography>
                      <Typography variant="body2">{formatAmount(detail.SellRate)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">{formatAmount(detail.SellAmount)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* P&L */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    P&L
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: detail.PNL >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 500
                    }}
                  >
                    {formatAmount(detail.PNL)}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };
  
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      height: '100%',
      px: { xs: 0, sm: 1 },
      pt: { xs: 1, sm: 2 },
      pb: { xs: 0, sm: 2 },
      position: 'relative',
    }}>
      
      {/* Filters */}
      <Paper 
        elevation={isMobile ? 0 : undefined} 
        sx={{ 
          p: isMobile ? '8px 12px' : 2, 
          mb: isMobile ? 1 : 3, 
          flexShrink: 0,
          bgcolor: isMobile ? 'transparent' : 'background.paper',
          boxShadow: isMobile ? 'none' : undefined,
          backgroundImage: isMobile ? 'none' : undefined
        }}
      >
        {isMobile ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: showMobileFilters ? 2 : 0
          }}>
            <Button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              startIcon={<FilterListIcon />}
              endIcon={showMobileFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              size="small"
              sx={{ 
                color: 'text.secondary',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                }
              }}
            >
              Filter
            </Button>
          </Box>
        ) : (
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        )}
        
        <Collapse in={!isMobile || showMobileFilters}>
          <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  // Auto-search when Start Date is changed
                  setPage(0);
                  fetchLedgerData({ startDate: newValue });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      style: { fontWeight: 600 }
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  // Auto-search when End Date is changed
                  setPage(0);
                  fetchLedgerData({ endDate: newValue });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      style: { fontWeight: 600 }
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}>
              <Button 
                variant="contained" 
                startIcon={<SearchIcon />} 
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleResetFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Reset
              </Button>
            </Box>
          </Grid>
          </Grid>
        </Collapse>
      </Paper>
      
      {/* Results */}
      <Paper sx={{ 
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%'
      }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : ledgerData.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <AssessmentIcon sx={{ 
                fontSize: 64, 
                color: theme.palette.grey[400],
                mb: 2
              }} />
              <Typography variant="h6" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                No Ledger Entries Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                No transactions found for the selected date range.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ 
              p: 1,
              willChange: 'scroll-position',
              WebkitOverflowScrolling: 'touch',
              overflow: 'auto',
              flex: 1
            }}>
              {ledgerData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((entry, index) => {
                const isDebit = parseFloat(entry.debit) > 0;
                return (
                <CollapsibleMobileCard
                  key={index}
                  headerLeft={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {formatDate(entry.date_time)}
                    </Typography>
                  }
                  headerRight={
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600,
                        color: isDebit ? '#F44336' : '#4CAF50'
                      }}
                    >
                      {isDebit ? '-' : '+'}₹{formatBalance(isDebit ? entry.debit : entry.credit)}
                    </Typography>
                  }
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Narration
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                        {entry.narration}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Balance
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                        ₹{formatBalance(entry.balance)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<InfoIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTransactionClick(entry);
                        }}
                      >
                        View Details
                      </Button>
                    </Grid>
                  </Grid>
                </CollapsibleMobileCard>
                );
              })}
            </Box>
          ) : (
            <TableContainer sx={{ 
              flex: 1,
              overflow: 'auto',
              overflowX: 'auto',
              minHeight: 0,
              willChange: 'scroll-position',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[100],
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[400],
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[500],
                },
              },
            }}>
            <Table 
              stickyHeader 
              aria-label="sticky table" 
              size={isMobile ? "small" : "medium"}
              sx={{
                tableLayout: 'auto',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Transaction Date</TableCell>
                  <TableCell>Narration</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell width="40px"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerData.map((entry, index) => (
                  <TableRow 
                    hover 
                    role="checkbox" 
                    tabIndex={-1} 
                    key={index}
                    onClick={() => handleTransactionClick(entry)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover .info-icon': {
                        opacity: 1
                      }
                    }}
                  >
                    <TableCell>{formatDate(entry.date_time)}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: isMobile ? '150px' : '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {entry.narration}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatBalance(entry.debit)}</TableCell>
                    <TableCell>{formatBalance(entry.credit)}</TableCell>
                    <TableCell>{formatBalance(entry.balance)}</TableCell>
                    <TableCell>
                      <InfoIcon 
                        className="info-icon"
                        sx={{ 
                          fontSize: '1.2rem',
                          color: theme.palette.primary.main,
                          opacity: 0.3,
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1,
                            transform: 'scale(1.1)'
                          }
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}
      </Paper>

      {ledgerData.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
          sx={{
            flexShrink: 0,
            width: '100%',
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            '& .MuiToolbar-root': {
              minHeight: '40px',
              px: 1
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              margin: 0,
              fontSize: '0.8rem'
            }
          }}
        />
      )}

      {/* Mobile Drawer */}
      {isMobile && selectedTransaction && (
        <Drawer
          anchor="bottom"
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: {
              height: '85vh',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              p: 0,
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Transaction Details
                </Typography>
                <IconButton 
                  onClick={handleCloseDrawer}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {formatDateTime(selectedTransaction.date_time)}
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ 
              p: 2, 
              flex: 1, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {/* Narration */}
              <Box sx={{ 
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Narration
                </Typography>
                <Typography variant="body2">
                  {selectedTransaction.narration}
                </Typography>
              </Box>

              {/* Transaction Type */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Type
                </Typography>
                <Chip 
                  label={selectedTransaction.ledger_type}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Bill ID */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Bill ID
                </Typography>
                <Typography variant="body2">
                  {selectedTransaction.bill_id}
                </Typography>
              </Box>

              {renderBillDetails()}
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Desktop Modal */}
      {!isMobile && selectedTransaction && (
        <Dialog
          open={Boolean(selectedTransaction)}
          onClose={() => setSelectedTransaction(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden'
            }
          }}
        >
          {/* Header */}
          <DialogTitle sx={{ 
            p: 2, 
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transaction Details
              </Typography>
              <IconButton 
                onClick={() => setSelectedTransaction(null)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle2" color="text.secondary">
              {formatDateTime(selectedTransaction.date_time)}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ 
              p: 2, 
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {/* Narration */}
              <Box sx={{ 
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Narration
                </Typography>
                <Typography variant="body2">
                  {selectedTransaction.narration}
                </Typography>
              </Box>

              {/* Transaction Type */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Type
                </Typography>
                <Chip 
                  label={selectedTransaction.ledger_type}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Bill ID */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Bill ID
                </Typography>
                <Typography variant="body2">
                  {selectedTransaction.bill_id}
                </Typography>
              </Box>

              {renderBillDetails()}
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default LedgerDetails;